import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Eye, ServerCrash } from 'lucide-react';
import type { ErpErrorLogToken } from '@sleed/bizcommerce-api';
import { SystemGrid, type SystemGridColumn } from '~/components/system/grid/system-grid';
import { Button } from '@/components/ui/button';
import { formatDateTime } from '~/lib/format-date';
import { useErpLogs } from '~/hooks/use-erp-logs';
import { ErpLogDetailDialog } from './erp-log-detail-dialog';

/** ERP logs — read-only SigNoz telemetry grid (no add/edit/delete). Drill-down for the dashboard ERP alert. */
export function ErpLogsGridView() {
	const { t } = useTranslation();
	const { data, isLoading, isError, refetch } = useErpLogs();
	const [detail, setDetail] = useState<ErpErrorLogToken | null>(null);

	const columns = useMemo<SystemGridColumn<ErpErrorLogToken>[]>(
		() => [
			{
				key: 'date',
				header: t('system.erpLogs.columns.date'),
				width: '170px',
				sortValue: l => (l.Timestamp ? String(l.Timestamp) : ''),
				exportValue: l => formatDateTime(l.Timestamp),
				render: l => <span className="truncate text-xs tabular-nums text-foreground-muted">{formatDateTime(l.Timestamp)}</span>
			},
			{
				key: 'severity',
				header: t('system.erpLogs.columns.severity'),
				width: '90px',
				align: 'center',
				sortValue: l => l.Severity ?? '',
				exportValue: l => l.Severity ?? '',
				render: l => <span className="rounded bg-danger/10 px-1.5 py-0.5 text-[10px] font-medium text-danger-fg">{l.Severity || '—'}</span>
			},
			{
				key: 'service',
				header: t('system.erpLogs.columns.service'),
				width: '170px',
				sortValue: l => l.Service ?? '',
				exportValue: l => l.Service ?? '',
				render: l => (
					<span className="truncate text-xs text-foreground-muted" title={l.Service ?? ''}>
						{l.Service || '—'}
					</span>
				)
			},
			{
				key: 'task',
				header: t('system.erpLogs.columns.task'),
				width: '200px',
				sortValue: l => l.Task ?? '',
				exportValue: l => l.Task ?? '',
				render: l => (
					<span className="truncate text-xs text-foreground-muted" title={l.Task ?? ''}>
						{l.Task || '—'}
					</span>
				)
			},
			{
				key: 'message',
				header: t('system.erpLogs.columns.message'),
				width: 'minmax(0,1fr)',
				sortValue: l => l.Message ?? '',
				exportValue: l => l.Message ?? '',
				render: l => (
					<span className="truncate text-sm text-foreground" title={l.Message ?? ''}>
						{l.Message}
					</span>
				)
			},
			{
				key: 'trace',
				header: t('system.erpLogs.columns.trace'),
				width: '200px',
				defaultVisible: false,
				exportValue: l => l.TraceId ?? '',
				render: l => (
					<span className="truncate font-mono text-[11px] text-foreground-subtle" title={l.TraceId ?? ''}>
						{l.TraceId || '—'}
					</span>
				)
			}
		],
		[t]
	);

	return (
		<>
			<SystemGrid<ErpErrorLogToken>
				gridId="erp-logs"
				title={t('system.erpLogs.title')}
				rows={data ?? []}
				loading={isLoading}
				error={isError}
				onRetry={() => void refetch()}
				getRowId={l => l.id || `${l.Timestamp}-${l.Message}`}
				columns={columns}
				searchPredicate={(l, q) => `${l.Message ?? ''} ${l.Service ?? ''} ${l.Task ?? ''} ${l.TraceId ?? ''}`.toLowerCase().includes(q)}
				defaultSort={{ key: 'date', dir: 'desc' }}
				emptyIcon={<ServerCrash size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
				exportFilename="erp-logs"
				rowActions={row => (
					<Button
						variant="ghost"
						size="icon"
						aria-label={t('system.erpLogs.viewDetail')}
						title={t('system.erpLogs.viewDetail')}
						onClick={() => setDetail(row)}>
						<Eye size={15} />
					</Button>
				)}
			/>
			<ErpLogDetailDialog open={detail != null} onOpenChange={o => !o && setDetail(null)} log={detail} />
		</>
	);
}
